import React, { useEffect, useState, useRef } from 'react';
import projectAPI from '../api/projectAPI';
import UserDashboard from './UserDashboard';
import {
  FaUserPlus, FaTimes, FaChevronRight, FaLink,
  FaUpload, FaFilePdf, FaFileWord, FaFileImage,
  FaFile, FaDownload, FaEye, FaSpinner, FaTrash,
  FaFileAlt,
} from 'react-icons/fa';

// ── Helpers ───────────────────────────────────────────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>{role}</span>
);
const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">{entry}</span>
          {i < chain.length - 1 && <FaChevronRight className="text-purple-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── File helpers ──────────────────────────────────────────────────────────────
const getFileType = (file) => {
  if (file.type.startsWith('image/'))  return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) return 'doc';
  return 'other';
};
const FileIcon = ({ fileType }) => {
  if (fileType === 'image') return <FaFileImage className="text-blue-500 text-xl shrink-0" />;
  if (fileType === 'pdf')   return <FaFilePdf   className="text-red-500 text-xl shrink-0" />;
  if (fileType === 'doc')   return <FaFileWord  className="text-blue-700 text-xl shrink-0" />;
  return <FaFile className="text-gray-500 text-xl shrink-0" />;
};
const fmtSize = (b) => b < 1024 ? `${b}B` : b < 1048576 ? `${(b/1024).toFixed(1)}KB` : `${(b/1048576).toFixed(1)}MB`;
const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

// ── FileUploadPanel ───────────────────────────────────────────────────────────
const FileUploadPanel = ({ projectId, existingFiles = [], onUploaded }) => {
  const [pending, setPending]       = useState([]);
  const [uploading, setUploading]   = useState(false);
  const [uploaded, setUploaded]     = useState(existingFiles);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  const addFiles = (fileList) => setPending(prev => [...prev, ...Array.from(fileList).map(f => ({
    file: f, name: f.name, size: f.size, fileType: getFileType(f),
    preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
  }))]);

  const handleUpload = async () => {
    if (!projectId) { alert('Project ID missing.'); return; }
    if (!pending.length) { alert('No files selected.'); return; }
    setUploading(true);
    try {
      const files = await Promise.all(pending.map(async pf => ({ base64: await toBase64(pf.file), name: pf.name, fileType: pf.fileType })));
      const res = await projectAPI.post(`/${projectId}/attachments`, { files });
      setUploaded(res.data.attachments); setPending([]);
      onUploaded?.(res.data.attachments);
      alert(`✅ ${files.length} file(s) uploaded!`);
    } catch { alert('❌ Upload failed.'); } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attachment?')) return;
    try { await projectAPI.delete(`/${projectId}/attachments/${id}`); setUploaded(prev => prev.filter(f => f._id !== id)); }
    catch { alert('❌ Delete failed.'); }
  };

  return (
    <div className="space-y-3">
      <div onClick={() => inputRef.current?.click()} onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center cursor-pointer hover:bg-purple-50 transition">
        <FaUpload className="mx-auto text-purple-400 text-xl mb-1" />
        <p className="text-sm text-gray-500">Click or drag & drop files here</p>
        <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF, DOC, DOCX</p>
        <input ref={inputRef} type="file" multiple
          accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} className="hidden" />
      </div>
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Ready to upload</p>
          {pending.map((pf, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              {pf.preview ? <img src={pf.preview} alt="" className="w-9 h-9 object-cover rounded" /> : <FileIcon fileType={pf.fileType} />}
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 truncate">{pf.name}</p><p className="text-xs text-gray-400">{fmtSize(pf.size)}</p></div>
              <button onClick={() => setPending(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><FaTrash /></button>
            </div>
          ))}
          <button onClick={handleUpload} disabled={uploading}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2">
            {uploading ? <><FaSpinner className="animate-spin" /> Uploading...</> : <><FaUpload /> Upload {pending.length} file(s)</>}
          </button>
        </div>
      )}
      {uploaded.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Uploaded ({uploaded.length})</p>
          {uploaded.map(f => (
            <div key={f._id} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              <FileIcon fileType={f.fileType} />
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 truncate">{f.name}</p><p className="text-xs text-gray-400">by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}</p></div>
              <div className="flex items-center gap-2">
                {f.fileType === 'image' && <button onClick={() => setPreviewUrl(f.url)} className="text-blue-500 hover:text-blue-700"><FaEye /></button>}
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700"><FaDownload /></a>
                <button onClick={() => handleDelete(f._id)} className="text-red-400 hover:text-red-600"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!uploaded.length && !pending.length && <p className="text-xs text-gray-400 text-center">No attachments yet.</p>}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-10 right-0 text-white text-2xl">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Roles that can forward ────────────────────────────────────────────────────
const FORWARD_ROLES = ['Marketing', 'Project Leads', 'TL', 'HR & Design', 'Office'];

// ─────────────────────────────────────────────────────────────────────────────
const WorkerProjectView = () => {
  const worker     = JSON.parse(localStorage.getItem('worker'));
  const workerId   = worker?._id;
  const workerRole = worker?.role;

  const [projects, setProjects]         = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm]     = useState('');
  const [loading, setLoading]           = useState(true);

  // Forward modal
  const [showForwardModal, setShowForwardModal]     = useState(false);
  const [forwardProject, setForwardProject]         = useState(null);
  const [forwardWorkers, setForwardWorkers]         = useState([]);
  const [forwardSelectedIds, setForwardSelectedIds] = useState([]);

  // Chain modal
  const [showChainModal, setShowChainModal] = useState(false);
  const [chainProject, setChainProject]     = useState(null);

  // Attachment modal
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachProject, setAttachProject]     = useState(null);

  // Report modal
  const [showReportModal, setShowReportModal]     = useState(false);
  const [reportProject, setReportProject]         = useState(null);
  const [reportDescription, setReportDescription] = useState('');
  const [reportFile, setReportFile]               = useState(null);
  const [reportFilePreview, setReportFilePreview] = useState(null);
  const [submittingReport, setSubmittingReport]   = useState(false);
  const reportFileRef = useRef(null);

  // View reports modal
  const [showViewReports, setShowViewReports]       = useState(false);
  const [viewReportsProject, setViewReportsProject] = useState(null);
  const [reportsList, setReportsList]               = useState([]);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await projectAPI.get('/my-projects');
      setProjects(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // ── Forward ───────────────────────────────────────────────────────────────
  const openForwardModal = async (project) => {
    setForwardProject(project); setForwardSelectedIds([]);
    try {
      const res = await projectAPI.get('/assignable-workers');
      const existingIds = (project.assignees || []).map(a => a.userId?.toString());
      setForwardWorkers(res.data.filter(w => !existingIds.includes(w._id)));
    } catch (err) { console.error(err); }
    setShowForwardModal(true);
  };

  const handleForward = async () => {
    if (!forwardSelectedIds.length) { alert('Select at least one worker.'); return; }
    try {
      await projectAPI.post(`/${forwardProject._id}/forward`, { assigneeIds: forwardSelectedIds });
      alert('✅ Project forwarded!'); setShowForwardModal(false); fetchProjects();
    } catch (err) { alert(err.response?.data?.error || '❌ Forward failed.'); }
  };

  // ── Report ────────────────────────────────────────────────────────────────
  const openReportModal = (project) => {
    setReportProject(project);
    setReportDescription('');
    setReportFile(null);
    setReportFilePreview(null);
    setShowReportModal(true);
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setReportFile(null);
    setReportFilePreview(null);
    setReportDescription('');
  };

  const handleReportFile = (f) => {
    if (!f) return;
    toBase64(f).then(b64 => {
      setReportFile(b64);
      setReportFilePreview(f.type.startsWith('image/') ? b64 : null);
    });
  };

  const submitReport = async () => {
    if (!reportDescription) { alert('Description is required.'); return; }
    setSubmittingReport(true);
    try {
      await projectAPI.post(`/${reportProject._id}/report`, {
        description: reportDescription,
        photo:       reportFile || null,
      });
      alert('✅ Project report submitted!');
      closeReportModal();
      fetchProjects();
    } catch (err) {
      alert(err.response?.data?.error || '❌ Failed to submit report.');
    } finally { setSubmittingReport(false); }
  };

  // ── View reports ──────────────────────────────────────────────────────────
  const openViewReports = async (project) => {
    setViewReportsProject(project); setReportsList([]);
    try {
      const res = await projectAPI.get(`/${project._id}/reports`);
      setReportsList(res.data.reports || []);
    } catch (err) { console.error(err); }
    setShowViewReports(true);
  };

  const filtered = projects.filter(p =>
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (statusFilter === 'All' || p.status === statusFilter)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-6 pt-20">
      <UserDashboard />

      {/* Header */}
      <div className="max-w-5xl mx-auto flex flex-wrap justify-between items-center mb-6 mt-3 gap-3">
        <div>
          <h2 className="text-3xl font-bold font-mono text-purple-700">My Projects</h2>
          <p className="text-sm text-gray-500 mt-0.5">{worker?.username} <RoleBadge role={workerRole} /></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {['All','Ongoing','On Hold','Completed'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${statusFilter === s ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto mb-4">
        <input type="text" placeholder="Search projects..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="border px-4 py-2 rounded-lg w-full md:w-80 text-sm" />
      </div>

      {/* Project Cards */}
      <div className="max-w-5xl mx-auto space-y-4">
        {loading && <p className="text-center text-gray-400 py-12">Loading projects…</p>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-xl p-12 text-center text-gray-400">No projects found.</div>
        )}

        {filtered.map(project => {
          const myEntry = project.assignees?.find(a => a.userId?.toString() === workerId);
          return (
            <div key={project._id} className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition">

              {/* Top row */}
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-gray-800 text-lg">{project.title}</h4>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      project.status === 'Completed' ? 'bg-green-100 text-green-700'
                      : project.status === 'On Hold'  ? 'bg-blue-100 text-blue-700'
                      : 'bg-yellow-100 text-yellow-700'}`}>{project.status}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{project.description}</p>
                </div>
                <div className="text-right text-xs text-gray-400 shrink-0">
                  {project.startDate && <p>Start: {project.startDate.slice(0,10)}</p>}
                  {project.endDate   && <p>End:   {project.endDate.slice(0,10)}</p>}
                </div>
              </div>

              {/* Created by */}
              {project.createdBy?.username && (
                <p className="text-xs text-gray-400 mt-2">
                  Created by: <span className="font-medium text-gray-600">{project.createdBy.username}</span>{' '}
                  <RoleBadge role={project.createdBy.role} />
                </p>
              )}

              {/* Who assigned to me */}
              {myEntry && (
                <p className="text-xs text-gray-400 mt-1">
                  Assigned to you by: <span className="font-medium text-purple-600">{myEntry.assignedBy}</span>
                  {' · '}{new Date(myEntry.assignedAt).toLocaleDateString('en-GB')}
                </p>
              )}

              {/* Chain trail */}
              {project.assignmentChain?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-400 mb-1">Assignment trail:</p>
                  <ChainTrail chain={project.assignmentChain} />
                </div>
              )}

              {/* All assignees */}
              {project.assignees?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 mb-1">All assignees:</p>
                  <div className="flex flex-wrap gap-2">
                    {project.assignees.map((a, i) => (
                      <div key={i} className={`flex items-center gap-1 rounded-full px-3 py-1 border text-xs ${a.userId?.toString() === workerId ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'}`}>
                        <span className="font-medium text-gray-700">{a.username}</span>
                        <RoleBadge role={a.role} />
                        {a.userId?.toString() === workerId && <span className="text-xs bg-purple-600 text-white px-1.5 py-0.5 rounded-full">You</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-4 flex flex-wrap items-center gap-2">

                {/* Files */}
                <button onClick={() => { setAttachProject(project); setShowAttachModal(true); }}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 rounded-lg px-3 py-1.5 transition">
                  <FaUpload /> {project.attachments?.length > 0 ? `Files (${project.attachments.length})` : 'Files'}
                </button>

                {/* Forward */}
                {FORWARD_ROLES.includes(workerRole) && project.status !== 'Completed' && (
                  <button onClick={() => openForwardModal(project)}
                    className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium border border-purple-200 hover:border-purple-400 rounded-lg px-3 py-1.5 transition">
                    <FaUserPlus /> Forward
                  </button>
                )}

                {/* Chain detail */}
                {project.assignmentChain?.length > 0 && (
                  <button onClick={() => { setChainProject(project); setShowChainModal(true); }}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium border border-gray-200 hover:border-gray-400 rounded-lg px-3 py-1.5 transition">
                    <FaLink /> Chain
                  </button>
                )}

                {/* Submit Report — Project Leads ONLY */}
                {workerRole === 'Project Leads' && (
                  <button onClick={() => openReportModal(project)}
                    className="flex items-center gap-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg px-3 py-1.5 transition">
                    <FaFileAlt /> Submit Report
                  </button>
                )}

                {/* View Reports */}
                {project.reports?.length > 0 && (
                  <button onClick={() => openViewReports(project)}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium border border-indigo-200 hover:border-indigo-400 rounded-lg px-3 py-1.5 transition">
                    <FaFileAlt /> Reports ({project.reports.length})
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── ATTACHMENT MODAL ─────────────────────────────────────────────────── */}
      {showAttachModal && attachProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowAttachModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Files</h3>
            <p className="text-sm text-gray-500 mb-2">"<span className="font-medium text-gray-700">{attachProject.title}</span>"</p>
            <p className="text-xs text-gray-400 mb-4">📎 Shared with all project assignees.</p>
            <FileUploadPanel projectId={attachProject._id} existingFiles={attachProject.attachments || []}
              onUploaded={(attachments) => {
                setProjects(prev => prev.map(p => p._id === attachProject._id ? { ...p, attachments } : p));
                setAttachProject(prev => ({ ...prev, attachments }));
              }} />
          </div>
        </div>
      )}

      {/* ── REPORT SUBMIT MODAL ───────────────────────────────────────────────── */}
      {showReportModal && reportProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={closeReportModal} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-xl font-bold text-indigo-700 mb-1 text-center">Project Report</h3>
            <p className="text-sm text-gray-500 text-center mb-4">"{reportProject.title}"</p>

            {/* Description */}
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              placeholder="Describe the project progress..."
              value={reportDescription}
              onChange={e => setReportDescription(e.target.value)}
              className="border rounded-lg w-full p-3 mb-4 focus:ring-2 focus:ring-indigo-400 outline-none resize-none"
              rows="4"
            />

            {/* File upload */}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachment <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div
              onClick={() => reportFileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleReportFile(e.dataTransfer.files[0]); }}
              className="border-2 border-dashed border-indigo-300 rounded-lg p-4 text-center cursor-pointer hover:bg-indigo-50 transition mb-3"
            >
              <FaUpload className="mx-auto text-indigo-400 text-xl mb-1" />
              <p className="text-sm text-gray-500">Click or drag & drop a file</p>
              <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF, DOC</p>
              <input
                ref={reportFileRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                className="hidden"
                onChange={e => { handleReportFile(e.target.files[0]); e.target.value = ''; }}
              />
            </div>

            {/* Image preview */}
            {reportFilePreview && (
              <div className="relative mb-3">
                <img src={reportFilePreview} alt="preview" className="w-full rounded-lg border" />
                <button
                  onClick={() => { setReportFile(null); setReportFilePreview(null); }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">
                  ✕
                </button>
              </div>
            )}

            {/* Non-image file indicator */}
            {reportFile && !reportFilePreview && (
              <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 mb-3">
                <FaFileAlt className="text-indigo-500 shrink-0" />
                <span className="text-sm text-indigo-700 flex-1">File attached</span>
                <button
                  onClick={() => { setReportFile(null); setReportFilePreview(null); }}
                  className="text-red-400 hover:text-red-600"><FaTimes /></button>
              </div>
            )}

            <button onClick={submitReport} disabled={submittingReport}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 rounded-lg flex items-center justify-center gap-2 font-semibold">
              {submittingReport ? <><FaSpinner className="animate-spin" /> Submitting...</> : 'Submit Report'}
            </button>
          </div>
        </div>
      )}

      {/* ── VIEW REPORTS MODAL ───────────────────────────────────────────────── */}
      {showViewReports && viewReportsProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowViewReports(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-indigo-700 mb-1">Project Reports</h3>
            <p className="text-sm text-gray-500 mb-4">"{viewReportsProject.title}"</p>
            {reportsList.length === 0
              ? <p className="text-center text-gray-400 py-8">No reports submitted yet.</p>
              : (
                <div className="space-y-4">
                  {reportsList.map((r, i) => (
                    <div key={i} className="border rounded-xl p-4 bg-indigo-50">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-semibold text-indigo-700">{r.submittedBy}</span>
                        <span className="text-xs text-gray-400">{new Date(r.submittedAt).toLocaleDateString('en-GB')}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">{r.description}</p>
                      {r.photo && <img src={r.photo} alt="report" className="w-full rounded-lg border" />}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      )}

      {/* ── FORWARD MODAL ────────────────────────────────────────────────────── */}
      {showForwardModal && forwardProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowForwardModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Forward Project</h3>
            <p className="text-sm text-gray-500 mb-3">"<span className="font-medium text-gray-700">{forwardProject.title}</span>"</p>
            {forwardProject.assignmentChain?.length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Current chain:</p>
                <ChainTrail chain={forwardProject.assignmentChain} />
                <div className="flex items-center gap-1 mt-1">
                  <FaChevronRight className="text-purple-300 text-xs" />
                  <span className="text-xs bg-purple-100 text-purple-700 border border-purple-300 px-2 py-0.5 rounded-full font-semibold">
                    {worker?.username} ({workerRole}) → ?
                  </span>
                </div>
              </div>
            )}
            <div className="border rounded-lg divide-y max-h-56 overflow-y-auto mb-4">
              {forwardWorkers.length === 0
                ? <p className="text-sm text-gray-400 p-4 text-center">No eligible workers.</p>
                : forwardWorkers.map(w => (
                  <label key={w._id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-purple-50 ${forwardSelectedIds.includes(w._id) ? 'bg-purple-50' : ''}`}>
                    <input type="checkbox" checked={forwardSelectedIds.includes(w._id)}
                      onChange={() => setForwardSelectedIds(prev => prev.includes(w._id) ? prev.filter(x => x !== w._id) : [...prev, w._id])}
                      className="accent-purple-600" />
                    <span className="flex-1 text-sm font-medium">{w.username}</span>
                    <RoleBadge role={w.role} />
                  </label>
                ))}
            </div>
            {forwardSelectedIds.length > 0 && <p className="text-xs text-purple-600 mb-3">{forwardSelectedIds.length} selected</p>}
            <button onClick={handleForward} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 rounded-lg">Forward Project</button>
          </div>
        </div>
      )}

      {/* ── CHAIN MODAL ──────────────────────────────────────────────────────── */}
      {showChainModal && chainProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowChainModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Assignment Chain</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{chainProject.title}</span>"</p>
            <div className="mb-4"><p className="text-xs text-gray-400 mb-2">Delegation path:</p><ChainTrail chain={chainProject.assignmentChain} /></div>
            <div>
              <p className="text-xs text-gray-400 mb-2">All assignees:</p>
              <div className="space-y-2">
                {chainProject.assignees?.map((a, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${a.userId?.toString() === workerId ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'}`}>
                    <span className="text-sm font-medium flex-1">{a.username}</span>
                    <RoleBadge role={a.role} />
                    <span className="text-xs text-gray-400">← {a.assignedBy}</span>
                    {a.userId?.toString() === workerId && <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">You</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerProjectView;